// Icon shim — re-exports @boxicons/react components under the names that
// the codebase historically used from @radix-ui/react-icons. The migration
// from Radix icons to Boxicons is absorbed by this single file: callers
// only changed their import path.
//
// Boxicons defaults to `width=24 height=24` and `fill="currentColor"`. Radix
// defaulted to `width=15`. To preserve the existing visual sizing, every
// icon below is wrapped in a thin component that overrides the default
// width/height to 15 — callers passing explicit `width`/`height` props
// still take precedence (the override is only applied when the caller
// didn't pass a size).

import { forwardRef } from 'react';
import type { ComponentType, SVGProps } from 'react';
import {
  Pulse,
  Archive,
  ArrowLeft,
  ArrowRight,
  Bell,
  Calendar,
  MessageDots,
  CheckCircle,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  GitCommit,
  X,
  XCircle,
  Dashboard,
  Fullscreen,
  Envelope,
  AlertTriangle,
  ArrowOutUpRightSquare,
  Eye,
  File,
  FileDetail,
  Folder,
  Bold,
  Italic,
  Cog,
  Globe,
  Group,
  DotsHorizontal,
  DotsVertical,
  Menu,
  Moon,
  Home,
  Image,
  InfoCircle,
  Keyboard,
  Layers,
  Link,
  ListUl,
  Lock,
  MagicWand,
  Search,
  Send,
  Edit,
  User,
  Plus,
  QuoteLeft,
  RefreshCw,
  Undo,
  Stop,
  Trash,
  ArrowFromBottom,
  ArrowToBottom,
} from '@boxicons/react';
import type { BoxIconProps } from '@boxicons/react';

// Radix icons defaulted to 15px. Boxicons defaults to 24px. Wrap every
// re-exported component so that callers who don't pass an explicit size
// keep the old visual scale.
type IconLike = ComponentType<BoxIconProps & { ref?: unknown }>;

function withRadixDefaults<P extends BoxIconProps>(
  Component: ComponentType<P>,
  displayName: string,
) {
  const Wrapped = forwardRef<SVGSVGElement, P>((props, ref) => {
    const merged = {
      width: props.width ?? 15,
      height: props.height ?? 15,
      ...props,
    } as P & { ref?: React.Ref<SVGSVGElement> };
    return <Component {...merged} ref={ref as never} />;
  });
  Wrapped.displayName = displayName;
  return Wrapped as unknown as ComponentType<SVGProps<SVGSVGElement> & BoxIconProps>;
}

// Direct, unambiguous remappings.
export const ActivityLogIcon = withRadixDefaults(Pulse as IconLike, 'ActivityLogIcon');
export const ArchiveIcon = withRadixDefaults(Archive as IconLike, 'ArchiveIcon');
export const ArrowLeftIcon = withRadixDefaults(ArrowLeft as IconLike, 'ArrowLeftIcon');
export const ArrowRightIcon = withRadixDefaults(ArrowRight as IconLike, 'ArrowRightIcon');
export const BellIcon = withRadixDefaults(Bell as IconLike, 'BellIcon');
export const CalendarIcon = withRadixDefaults(Calendar as IconLike, 'CalendarIcon');
export const ChatBubbleIcon = withRadixDefaults(MessageDots as IconLike, 'ChatBubbleIcon');
export const CheckCircledIcon = withRadixDefaults(CheckCircle as IconLike, 'CheckCircledIcon');
export const CheckIcon = withRadixDefaults(Check as IconLike, 'CheckIcon');
export const ChevronDownIcon = withRadixDefaults(ChevronDown as IconLike, 'ChevronDownIcon');
export const ChevronLeftIcon = withRadixDefaults(ChevronLeft as IconLike, 'ChevronLeftIcon');
export const ChevronRightIcon = withRadixDefaults(ChevronRight as IconLike, 'ChevronRightIcon');
export const ChevronUpIcon = withRadixDefaults(ChevronUp as IconLike, 'ChevronUpIcon');
export const ClockIcon = withRadixDefaults(Clock as IconLike, 'ClockIcon');
export const CommitIcon = withRadixDefaults(GitCommit as IconLike, 'CommitIcon');
// Radix had two visually-similar X variants; Boxicons collapses them to one `X`.
export const Cross1Icon = withRadixDefaults(X as IconLike, 'Cross1Icon');
export const Cross2Icon = withRadixDefaults(X as IconLike, 'Cross2Icon');
export const CrossCircledIcon = withRadixDefaults(XCircle as IconLike, 'CrossCircledIcon');
export const DashboardIcon = withRadixDefaults(Dashboard as IconLike, 'DashboardIcon');
export const DotsHorizontalIcon = withRadixDefaults(DotsHorizontal as IconLike, 'DotsHorizontalIcon');
export const DotsVerticalIcon = withRadixDefaults(DotsVertical as IconLike, 'DotsVerticalIcon');
export const EnterFullScreenIcon = withRadixDefaults(Fullscreen as IconLike, 'EnterFullScreenIcon');
export const EnvelopeClosedIcon = withRadixDefaults(Envelope as IconLike, 'EnvelopeClosedIcon');
export const ExclamationTriangleIcon = withRadixDefaults(AlertTriangle as IconLike, 'ExclamationTriangleIcon');
export const ExternalLinkIcon = withRadixDefaults(ArrowOutUpRightSquare as IconLike, 'ExternalLinkIcon');
// Boxicons doesn't have a "Show" — the eye is the universal show/preview affordance.
export const EyeOpenIcon = withRadixDefaults(Eye as IconLike, 'EyeOpenIcon');
export const FileIcon = withRadixDefaults(File as IconLike, 'FileIcon');
// FileDetail = file outline with horizontal lines — the FileText analogue.
export const FileTextIcon = withRadixDefaults(FileDetail as IconLike, 'FileTextIcon');
export const FolderIcon = withRadixDefaults(Folder as IconLike, 'FolderIcon');
export const FontBoldIcon = withRadixDefaults(Bold as IconLike, 'FontBoldIcon');
export const FontItalicIcon = withRadixDefaults(Italic as IconLike, 'FontItalicIcon');
export const GearIcon = withRadixDefaults(Cog as IconLike, 'GearIcon');
export const GlobeIcon = withRadixDefaults(Globe as IconLike, 'GlobeIcon');
export const GroupIcon = withRadixDefaults(Group as IconLike, 'GroupIcon');
export const HamburgerMenuIcon = withRadixDefaults(Menu as IconLike, 'HamburgerMenuIcon');
export const HomeIcon = withRadixDefaults(Home as IconLike, 'HomeIcon');
export const ImageIcon = withRadixDefaults(Image as IconLike, 'ImageIcon');
export const InfoCircledIcon = withRadixDefaults(InfoCircle as IconLike, 'InfoCircledIcon');
export const KeyboardIcon = withRadixDefaults(Keyboard as IconLike, 'KeyboardIcon');
export const LayersIcon = withRadixDefaults(Layers as IconLike, 'LayersIcon');
export const Link2Icon = withRadixDefaults(Link as IconLike, 'Link2Icon');
export const ListBulletIcon = withRadixDefaults(ListUl as IconLike, 'ListBulletIcon');
export const LockClosedIcon = withRadixDefaults(Lock as IconLike, 'LockClosedIcon');
export const MagicWandIcon = withRadixDefaults(MagicWand as IconLike, 'MagicWandIcon');
export const MagnifyingGlassIcon = withRadixDefaults(Search as IconLike, 'MagnifyingGlassIcon');
export const MoonIcon = withRadixDefaults(Moon as IconLike, 'MoonIcon');
export const PaperPlaneIcon = withRadixDefaults(Send as IconLike, 'PaperPlaneIcon');
export const Pencil1Icon = withRadixDefaults(Edit as IconLike, 'Pencil1Icon');
export const PersonIcon = withRadixDefaults(User as IconLike, 'PersonIcon');
export const PlusIcon = withRadixDefaults(Plus as IconLike, 'PlusIcon');
export const QuoteIcon = withRadixDefaults(QuoteLeft as IconLike, 'QuoteIcon');
export const ReloadIcon = withRadixDefaults(RefreshCw as IconLike, 'ReloadIcon');
export const ResetIcon = withRadixDefaults(Undo as IconLike, 'ResetIcon');
export const StopIcon = withRadixDefaults(Stop as IconLike, 'StopIcon');
export const TrashIcon = withRadixDefaults(Trash as IconLike, 'TrashIcon');
export const UpdateIcon = withRadixDefaults(RefreshCw as IconLike, 'UpdateIcon');
// Boxicons doesn't ship an "Upload" icon — `ArrowFromBottom` is the
// closest visual analogue (arrow rising from a horizontal line).
export const UploadIcon = withRadixDefaults(ArrowFromBottom as IconLike, 'UploadIcon');
export const DownloadIcon = withRadixDefaults(ArrowToBottom as IconLike, 'DownloadIcon');
